import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AutoResizeTextarea from "../components/AutoResizeTextarea";
import DatePicker from "react-datepicker";
import { enAU } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import { toZonedTime, fromZonedTime } from "date-fns-tz";
import FormOptions from "../components/FormOptions";
import TermsAndFAQ from "../components/TermsAndFAQ";
import "../styles/Booking.css";

export default function Booking() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [picnicDate, setPicnicDate] = useState(null);
  const [preferredLocation, setPreferredLocation] = useState("");
  const [picnicSetting, setPicnicSetting] = useState("");
  const [picnicStyle, setPicnicStyle] = useState("");
  const [celebrationType, setCelebrationType] = useState("");
  const [guestNo, setGuestNo] = useState("");
  const [additions, setAdditions] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [bookingHeader, setBookingHeader] = useState([]);
  const [awardData, setAwardData] = useState([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const navigate = useNavigate();

  const nameRef = useRef();
  const emailRef = useRef();
  const phoneRef = useRef();
  const dateRef = useRef();
  const guestRef = useRef();
  const settingRef = useRef();
  const styleRef = useRef();
  const locationRef = useRef();

  useEffect(() => {
    const fetchImage = async () => {
      setImageLoading(true);
      try {
        const [bookingRes, awardRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/images/booking`),
          fetch(`${process.env.REACT_APP_API_URL}/api/images/main`),
        ]);

        if (!bookingRes.ok)
          throw new Error(
            bookingRes.message || "Failed to fetch booking image",
          );
        if (!awardRes.ok)
          throw new Error(awardRes.message || "Failed to fetch Award image");

        const bookingData = await bookingRes.json();
        const awardData = await awardRes.json();

        setBookingHeader(bookingData.data.header);
        setAwardData(awardData.data.award);
      } catch (err) {
        setImageError(err || "Failed to fetch image");
      } finally {
        setImageLoading(false);
      }
    };
    fetchImage();
  }, []);

  // Session storage for form data
  useEffect(() => {
    const savedBooking = sessionStorage.getItem("bookingForm");

    if (savedBooking) {
      const data = JSON.parse(savedBooking);

      setName(data.name || "");
      setEmail(data.email || "");
      setPhone(data.phone || "");
      setPicnicDate(data.picnicDate ? new Date(data.picnicDate) : null);
      setPreferredLocation(data.preferredLocation || "");
      setPicnicSetting(data.picnicSetting || "");
      setPicnicStyle(data.picnicStyle || "");
      setCelebrationType(data.celebrationType || "");
      setGuestNo(data.guestNo || "");
      setAdditions(data.additions || []);
      setAdditionalNotes(data.additionalNotes || []);
      setTermsAccepted(data.termsAccepted || false);
    }
  }, []);

  useEffect(() => {
    const bookingForm = {
      name,
      email,
      phone,
      picnicDate,
      preferredLocation,
      picnicSetting,
      picnicStyle,
      celebrationType,
      guestNo,
      additions,
      additionalNotes,
      termsAccepted,
    };

    sessionStorage.setItem("bookingForm", JSON.stringify(bookingForm));
  }, [
    name,
    email,
    phone,
    picnicDate,
    preferredLocation,
    picnicSetting,
    picnicStyle,
    celebrationType,
    guestNo,
    additions,
    additionalNotes,
    termsAccepted,
  ]);

  if (imageLoading) {
    return <div>Loading...</div>;
  }

  if (imageError) {
    return <div>Unable to load images.</div>;
  }

  // Helper function to restrict time for datepicker minTime and maxTime
  const BRISBANE_TZ = "Australia/Brisbane";

  // Always builds min/max window in true Brisbane time, converted to the
  // correct UTC instant for comparison against selected/onChange
  const setTime = (hour, minutes = 0) => {
    const now = new Date();
    const wallClock = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hour,
      minutes,
      0,
      0,
    );
    return fromZonedTime(wallClock, BRISBANE_TZ);
  };

  // "Today" as Brisbane sees it, not the visitor's machine
  const brisbaneNow = () => toZonedTime(new Date(), BRISBANE_TZ);

  const validate = () => {
    const newErrors = {};

    let firstInvalidRef = null;

    if (!name.trim()) {
      newErrors.name = true;
      firstInvalidRef ??= nameRef;
    }
    if (!email.trim()) {
      newErrors.email = true;
      firstInvalidRef ??= emailRef;
    }
    if (!phone.trim()) {
      newErrors.phone = true;
      firstInvalidRef ??= phoneRef;
    }
    if (!picnicDate) {
      newErrors.picnicDate = true;
      firstInvalidRef ??= dateRef;
    }
    if (!guestNo) {
      newErrors.guestNo = true;
      firstInvalidRef ??= guestRef;
    }
    if (!picnicSetting) {
      newErrors.picnicSetting = true;
      firstInvalidRef ??= settingRef;
    }
    if (!picnicStyle) {
      newErrors.picnicStyle = true;
      firstInvalidRef ??= styleRef;
    }

    if (!preferredLocation.trim()) {
      newErrors.preferredLocation = true;
      firstInvalidRef ??= locationRef;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fill in all required fields.");

      firstInvalidRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      setTimeout(() => {
        firstInvalidRef?.current?.focus?.();
      }, 300);

      return false;
    }

    return true;
  };

  // Handle form submission
  const submitHandler = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    // Validation for number of guests
    const guestCount = Number(guestNo);
    if (
      !Number.isInteger(guestCount) ||
      isNaN(guestCount) ||
      guestCount < 1 ||
      guestCount > 20
    ) {
      toast.error(
        errors || "Number of guests must be a number between 1 and 20.",
      );
      return;
    }

    if (!termsAccepted) {
      toast.error("Please accept the Terms and Conditions.");
      return;
    }

    const formData = {
      name,
      email,
      phone,
      picnicDate,
      preferredLocation,
      picnicSetting,
      picnicStyle,
      celebrationType,
      guestNo: guestCount,
      additions,
      additionalNotes,
      termsAccepted,
    };

    setLoading(true);

    const REACT_APP_API_URL = process.env.REACT_APP_API_URL;
    if (!REACT_APP_API_URL) {
      toast.error("Configuration error. Please try again later.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${REACT_APP_API_URL}/api/bookings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to submit booking.");
      }

      toast.success(
        "Thank you for Booking with Bribie Island Picnics! Your booking has been submitted successfully! We will contact you shortly.",
      );
      sessionStorage.removeItem("bookingForm");

      setTimeout(() => {
        navigate("/");
      }, 7000);
    } catch (err) {
      toast.error(
        err.message ||
          "There was an error submitting your booking. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="booking">
      {/* Booking Header */}
      <section className="booking__intro--container">
        <div className="booking__intro--header">
          <img
            src={bookingHeader[0]?.src}
            alt={bookingHeader[0]?.alt}
            className="groups__intro--image"
          />
          <div className="booking__intro--text">
            <h1>Book Your Special Moments</h1>
            <p>
              Luxury picnics designed for unforgettable life experiences and
              celebrations
            </p>
          </div>
          <div className="booking_award--container-right">
            <img
              className="booking_award--image"
              src={awardData[2]?.src}
              alt={awardData[2]?.alt}
            />
          </div>
        </div>
      </section>

      {/* ------------------- Booking Form ------------------*/}
      <section className="booking__form--container">
        <div className="booking__form--header">
          <h2>Last Minute Bookings (24 to 72 hours before picnic)</h2>
          <p>
            For last minute bookings, please call us on Ph: 0493 294 665 to
            check availiability and secure your booking. We will do our best to
            accommodate your request.
          </p>
        </div>
        <span className="booking__form--item-required-note">
          * Required Fields
        </span>
        <form onSubmit={submitHandler} className="booking__form" noValidate>
          <div className="booking__form--grid">
            {/* Name */}
            <div className="booking__form--item">
              <label className="booking__form--item-label" htmlFor="name">
                Name <span>*</span>
              </label>
              <input
                className={`booking__form--item-input ${
                  errors.name ? "input-error" : ""
                }`}
                ref={nameRef}
                placeholder="Enter your name"
                type="text"
                id="name"
                name="name"
                autoComplete="name"
                aria-label="customer name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            {/* Email */}
            <div className="booking__form--item">
              <label className="booking__form--item-label" htmlFor="email">
                Email <span>*</span>
              </label>
              <input
                className={`booking__form--item-input ${
                  errors.email ? "input-error" : ""
                }`}
                ref={emailRef}
                placeholder="Enter your email"
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                aria-label="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {/* Phone */}
            <div className="booking__form--item">
              <label className="booking__form--item-label" htmlFor="phone">
                Phone <span>*</span>
              </label>
              <input
                className={`booking__form--item-input ${
                  errors.phone ? "input-error" : ""
                }`}
                ref={phoneRef}
                placeholder="Enter your phone number"
                type="tel"
                id="phone"
                name="phone"
                autoComplete="tel"
                aria-label="phone number"
                pattern="[0-9+\s-]{7,15}"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>

            {/* Picnic Date & Time */}
            <div className="booking__form--item">
              <label className="booking__form--item-label">
                Picnic Date & Time <span>*</span>
              </label>

              <div
                className={`booking__form--item-input datepicker-input ${
                  errors.picnicDate ? "input-error" : ""
                }`}
                ref={dateRef}
              >
                <div className="booking__form--datepicker-container">
                  <DatePicker
                    className={`booking__form--datePicker-placeholder booking__class--datepicker ${
                      picnicDate ? "selected" : ""
                    }`}
                    wrapperClassName="booking__wrapper-class--datepicker"
                    placeholderText="Select your preferred DATE and TIME of picnic"
                    aria-label="picnic-date and time"
                    selected={picnicDate}
                    onChange={(date) => setPicnicDate(date)}
                    shouldCloseOnSelect
                    locale={enAU}
                    timeZone={BRISBANE_TZ}
                    dateFormat="dd/MM/yyyy h:mm aa"
                    showTimeSelect
                    timeIntervals={15}
                    minTime={setTime(6)}
                    maxTime={setTime(18)}
                    minDate={brisbaneNow}
                  />

                  {picnicDate && (
                    <button
                      type="button"
                      onClick={() => setPicnicDate(null)}
                      className="booking__form--reset-time-btn"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Number of Guests */}
            <div className="booking__form--item">
              <label className="booking__form--item-label" htmlFor="guestNo">
                Number of Guests (1-20) <span>*</span>
              </label>
              <input
                className={`booking__form--item-input ${
                  errors.guestNo ? "input-error" : ""
                }`}
                ref={guestRef}
                placeholder="Select the number of picnic guests"
                type="number"
                inputMode="numeric"
                id="guestNo"
                min="1"
                max="20"
                aria-label="Number of guests"
                value={guestNo}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty input
                  if (value === "") {
                    setGuestNo("");
                    e.target.setCustomValidity("");
                    return;
                  }
                  const num = Number(value);
                  if (isNaN(num) || num < 1 || num > 20) {
                    e.target.setCustomValidity("Pick a number 1-20");
                  } else {
                    e.target.setCustomValidity("");
                    setGuestNo(num);
                  }
                }}
                required
              />
            </div>
            {/* Picnic Type */}
            <div className="booking__form--item-dropdown">
              <label
                className="booking__form--item-label"
                htmlFor="picnicSetting"
              >
                Picnic Setting <span>*</span>
              </label>
              <select
                id="picnicSetting"
                className={`booking__form--item-input ${
                  errors.picnicSetting ? "input-error" : ""
                }`}
                ref={settingRef}
                aria-label="Select a picnic setting"
                value={picnicSetting}
                onChange={(e) => setPicnicSetting(e.target.value)}
                required
              >
                <option value="" hidden>
                  Select a picnic setting...
                </option>
                <option value="" disabled>
                  Select a Picnic Setting...
                </option>
                <optgroup label="Couple Picnics">
                  <option value="Bohemian Luxury">Bohemian Luxury</option>
                  <option value="Elegant Dining Height">
                    Elegant Dining Height
                  </option>
                  <option value="Couples Picnic Table">
                    Couples Picnic Table
                  </option>
                  <option value="Couples Luxury Coastal Cabana">
                    Couples Luxury Coastal Cabana
                  </option>
                  <option value="Couples Luxury Laced Teepee">
                    Couples Luxury Laced Teepee
                  </option>
                  <option value="Couples Signature Cabana">
                    Couples Signature Cabana
                  </option>
                </optgroup>
                <optgroup label="Group Picnics">
                  <option value="Group Picnic">Group Picnic</option>
                </optgroup>
                <optgroup label="Proposal Picnics">
                  <option value="Proposal Picnic For 2">
                    Proposal Picnic For 2
                  </option>
                  <option value="Create Your Own Picnic">
                    Create Your Own Picnic
                  </option>
                </optgroup>
                <optgroup label="Special Events">
                  <option value="Special Events Signature Set-Up">
                    Special Events Signature Set-Up
                  </option>
                  <option value="Special Events Enhance Your Event">
                    Special Events Enhance Your Event
                  </option>
                </optgroup>
              </select>
            </div>
            {/* Picnic Style */}
            <div className="booking__form--item-dropdown">
              <label
                className="booking__form--item-label"
                htmlFor="picnicStyle"
              >
                Picnic Style <span>*</span>
              </label>

              <select
                id="picnicStyle"
                className={`booking__form--item-input ${
                  errors.picnicStyle ? "input-error" : ""
                }`}
                ref={styleRef}
                aria-label="Select a picnic style"
                value={picnicStyle}
                onChange={(e) => setPicnicStyle(e.target.value)}
                required
              >
                <option value="" style={{ color: "lightgray" }} hidden>
                  Select a picnic style...
                </option>
                <option value="" disabled>
                  Select a Picnic Style...
                </option>
                <option value="Black">Black</option>
                <option value="Blue">Blue</option>
                <option value="LavenderPurple">Lavender Purple</option>
                <option value="Macrame">Macrame</option>
                <option value="Natural/Beige">Natural Beige</option>
                <option value="PastelPink">Pastel Pink</option>
                <option value="Red">Red</option>
                <option value="Rust/Autumn ">Rust/Autumn </option>
                <option value="SageGreen">Sage Green</option>
                <option value="White">White</option>
              </select>
            </div>
            {/* Celebration Type */}
            <div className="booking__form--item">
              <label
                className="booking__form--item-label"
                htmlFor="celebrationType"
              >
                Type of Celebration
              </label>
              <input
                className="booking__form--item-input"
                placeholder="Enter the type of celebration (eg. birthday etc.)"
                type="text"
                id="celebrationType"
                aria-label="Type of celebration"
                value={celebrationType}
                onChange={(e) => setCelebrationType(e.target.value)}
              />
            </div>
            {/* Preferred Location*/}
            <div className="booking__form--item">
              <label
                className="booking__form--item-label"
                htmlFor="preferredLocation"
              >
                Preferred Location <span>*</span>
              </label>
              <input
                className={`booking__form--item-input ${
                  errors.preferredLocation ? "input-error" : ""
                }`}
                ref={locationRef}
                placeholder="Enter your preferred location"
                type="text"
                id="preferredLocation"
                aria-label="preferred location"
                value={preferredLocation}
                onChange={(e) => setPreferredLocation(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Select form Options */}
          <section className="formOptions__container">
            <FormOptions value={additions} onChange={setAdditions} />
          </section>

          {/* Additional Notes */}
          <div className="booking__form--group-textbox-container">
            <AutoResizeTextarea
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes([e.target.value])}
            />
          </div>

          <section className="booking__form--terms-container">
            <TermsAndFAQ
              termsAccepted={termsAccepted}
              setTermsAccepted={setTermsAccepted}
            />
          </section>

          {/* Submit Button */}
          <div className="booking__form--button-container">
            <button
              type="submit"
              className="booking__form--button-continue my-btn"
              disabled={loading}
              // onClick={submitHandler}
            >
              {loading ? "Submitting..." : "Submit Booking"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
